import React from 'react';
import {
    CodecBestQuality,
    CodecDefault,
    codecName,
    loadSettings,
    PreferredCodec,
    Settings,
    VideoDisplayMode,
} from './settings';
import { LiquidGlassCard } from './components/glass/LiquidGlassCard';
import { LiquidButton } from './components/glass/LiquidButton';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export interface SettingDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    updateName: (s: string) => void;
    saveSettings: (s: Settings) => void;
    role?: string;
}

const getAvailableCodecs = (): PreferredCodec[] => {
    if ('getCapabilities' in RTCRtpSender) {
        return RTCRtpSender.getCapabilities('video')?.codecs ?? [];
    }
    return [];
};

const NativeCodecs = getAvailableCodecs();

export const SettingDialog = ({
    open,
    setOpen,
    updateName,
    saveSettings,
    role,
}: SettingDialogProps) => {
    const [settingsInput, setSettingsInput] = React.useState(loadSettings);

    const doSubmit = () => {
        saveSettings(settingsInput);
        updateName(settingsInput.name ?? '');
        setOpen(false);
    };

    const { name, preferCodec, displayMode, framerate } = settingsInput;
    const isReceiver = role === 'receiver';

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setOpen(false)}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                    />

                    {/* Dialog */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ duration: 0.2 }}
                            className="w-full max-w-md"
                        >
                            <LiquidGlassCard className="border-white/10 bg-white/5 backdrop-blur-xl">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-2xl font-semibold text-white">Settings</h2>
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="text-white/60 hover:text-white transition-colors"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Form */}
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        doSubmit();
                                    }}
                                    className="space-y-4"
                                >
                                    {/* Username */}
                                    <div>
                                        <label className="block text-white/80 text-sm mb-2">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            autoFocus
                                            value={name}
                                            onChange={(e) =>
                                                setSettingsInput((c) => ({
                                                    ...c,
                                                    name: e.target.value,
                                                }))
                                            }
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm"
                                        />
                                    </div>

                                    {/* Codec */}
                                    {!isReceiver && NativeCodecs.length > 0 && (
                                        <div>
                                            <label className="block text-white/80 text-sm mb-2">
                                                Preferred Codec
                                            </label>
                                            <select
                                                value={
                                                    preferCodec
                                                        ? `${preferCodec.mimeType}|${preferCodec.sdpFmtpLine || ''}`
                                                        : 'default'
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === 'default') {
                                                        setSettingsInput((c) => ({
                                                            ...c,
                                                            preferCodec: CodecDefault,
                                                        }));
                                                    } else if (value === 'best') {
                                                        setSettingsInput((c) => ({
                                                            ...c,
                                                            preferCodec: CodecBestQuality,
                                                        }));
                                                    } else {
                                                        const [mimeType, sdpFmtpLine] =
                                                            value.split('|');
                                                        const codec = NativeCodecs.find(
                                                            (c) =>
                                                                c.mimeType === mimeType &&
                                                                (c.sdpFmtpLine || '') === sdpFmtpLine
                                                        );
                                                        if (codec) {
                                                            setSettingsInput((c) => ({
                                                                ...c,
                                                                preferCodec: codec,
                                                            }));
                                                        }
                                                    }
                                                }}
                                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm appearance-none cursor-pointer"
                                            >
                                                <option value="best">Preset: Best Quality</option>
                                                <option value="default">Preset: Browser Default</option>
                                                {NativeCodecs.map((codec) => {
                                                    const key = `${codec.mimeType}|${codec.sdpFmtpLine || ''}`;
                                                    const label =
                                                        codecName(codec.mimeType) +
                                                        (codec.sdpFmtpLine
                                                            ? ` (${codec.sdpFmtpLine})`
                                                            : '');
                                                    return (
                                                        <option key={key} value={key}>
                                                            {label}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    )}

                                    {/* Display Mode */}
                                    <div>
                                        <label className="block text-white/80 text-sm mb-2">
                                            Display Mode
                                        </label>
                                        <select
                                            value={displayMode}
                                            onChange={(e) =>
                                                setSettingsInput((c) => ({
                                                    ...c,
                                                    displayMode: e.target.value as VideoDisplayMode,
                                                }))
                                            }
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm appearance-none cursor-pointer"
                                        >
                                            {Object.values(VideoDisplayMode).map((mode) => (
                                                <option key={mode} value={mode}>
                                                    {mode}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* FrameRate */}
                                    {!isReceiver && (
                                    <div>
                                            <label className="block text-white/80 text-sm mb-2">
                                                FrameRate
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={framerate}
                                                onChange={(e) =>
                                                    setSettingsInput((c) => ({
                                                        ...c,
                                                        framerate: parseInt(e.target.value) || 30,
                                                    }))
                                                }
                                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 backdrop-blur-sm"
                                            />
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-3 pt-4">
                                        <LiquidButton
                                            type="button"
                                            onClick={() => setOpen(false)}
                                            variant="ghost"
                                            className="flex-1 bg-white/5 hover:bg-white/10 text-white border-0"
                                        >
                                            CANCEL
                                        </LiquidButton>
                                        <LiquidButton
                                            type="submit"
                                            className="flex-1 bg-gradient-to-b from-gray-600/80 to-gray-700/80 hover:from-gray-500/80 hover:to-gray-600/80 text-white border-0 shadow-2xl"
                                        >
                                            SAVE
                                        </LiquidButton>
                                    </div>
                                </form>
                            </LiquidGlassCard>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
